import { useEffect, useRef, useState } from "react";
import { KeyRound, ShieldAlert } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { BrandButton } from "@/components/kit/BrandButton";
import { EmptyState } from "@/components/kit/EmptyState";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/ppcp";
import { PAGES } from "@/constants/testIds";

/** Magic link landing. WordPress issues the token; PPCP exchanges it for a session. */
export default function OpenLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const attempted = useRef(false);

  useEffect(() => {
    // Tokens are single-use: guard against React re-invocation burning the nonce.
    if (attempted.current) return;
    attempted.current = true;

    const token = params.get("token");
    if (!token) {
      setError("No sign-in link was provided.");
      return;
    }

    api
      .post("/auth/magic", { token })
      .then(async () => {
        await refresh();
        // Replace history so the token never sits in the back stack.
        navigate("/library", { replace: true });
      })
      .catch(async (err) => {
        // A reload or retry of /open burns the single-use token. If the session was
        // already established, honour it instead of showing a dead end.
        if (await refresh()) {
          navigate("/library", { replace: true });
          return;
        }
        setError(err.response?.data?.detail ?? "We couldn't open your program.");
      });
  }, [params, navigate, refresh]);

  return (
    <AppShell title="Opening your program" showBack={false} nav={false} size="reading">
      <div data-testid={PAGES.openLink} className="contents">
        {error ? (
          <EmptyState icon={ShieldAlert} title="This link didn't work" description={error}>
            <BrandButton
              as="a"
              name="open-retry"
              href="https://www.plattershare.com/sign-in/"
              rel="noopener"
            >
              Reopen from Plattershare
            </BrandButton>
          </EmptyState>
        ) : (
          <EmptyState
            icon={KeyRound}
            title="Signing you in"
            description="Verifying your secure link with Plattershare…"
          />
        )}
      </div>
    </AppShell>
  );
}
